import { task } from 'mobx-task'
import once from 'lodash/once'
import { observable, action, computed, makeObservable } from 'mobx'
import { NOTIFICATION_TYPES } from './notification-types'
import {
  Subject,
  Observable,
  of,
  Subscription,
  from,
  firstValueFrom,
} from 'rxjs'
import { catchError, share, startWith, pairwise, map } from 'rxjs/operators'
import { tailMap } from 'utils/rx-util'
import {
  NotificationPreferences,
  NotificationMediaPreferences,
  NotificationTypesMedia,
} from 'notifications/dtos/notifications'
import Member from 'iam/models/Member'
import { AlertFactory } from 'alerts/Alert'
import { NotificationsPort } from '../ProfileViewModel'
import { maybeParseDate } from 'utils/date-util'

/**
 * View model for the Notifications tab.
 */
export default class ProfileNotificationsViewModel {
  /**
   * Subscription for Rx stuff.
   */
  private subscription = new Subscription()

  /**
   * Save requests.
   */
  private saveRequests = new Subject()

  /**
   * Master switch.
   */
  @observable master = new NotificationType(
    '',
    'All Notifications',
    '',
    null,
    () => this.queueSave(),
  )

  /**
   * Notification types.
   */
  @observable types = NOTIFICATION_TYPES.map(
    (spec) =>
      new NotificationType(
        spec.id,
        spec.title,
        spec.description,
        this.master,
        () => this.queueSave(),
      ),
  )

  /**
   * Syncing pipeline.
   */
  private sync$: Observable<SaveResult> = this.saveRequests.pipe(
    tailMap(() =>
      from(this.save(this.collectPreferences())).pipe(
        map(() => ({ type: 'Success' }) as SaveResult),
        catchError((err) =>
          of({
            type: 'Failed',
            error: err,
          } as SaveResult),
        ),
      ),
    ),
    share(),
  )

  /**
   * Constructor.
   */
  constructor(
    private member: Member,
    private newAlert: AlertFactory,
    private notificationsPort: NotificationsPort,
  ) {
    makeObservable(this)
  }

  /**
   * Collects the preferences to a payload.
   */
  collectPreferences(): NotificationPreferences {
    const toPreferences = (
      type: NotificationType,
    ): NotificationMediaPreferences => ({
      email: {
        enabled: type.isEnabled(NotificationMedium.Email),
        snoozed_until: type
          .mediumSnoozeDate(NotificationMedium.Email)
          ?.toISOString(),
      },
      push: {
        enabled: type.isEnabled(NotificationMedium.Push),
        snoozed_until: type
          .mediumSnoozeDate(NotificationMedium.Push)
          ?.toISOString(),
      },
    })

    return {
      media: toPreferences(this.master),
      types_media: this.types.reduce((collected, type) => {
        collected[type.id] = toPreferences(type)
        return collected
      }, {} as NotificationTypesMedia),
    }
  }

  /**
   * Activates the view model.
   */
  activate() {
    this.fetchNotificationPreferences()
    this.subscription = this.sync$
      .pipe(
        // Map the result to alerts
        map((result) => {
          if (result.type === 'Failed') {
            return this.newAlert().fromError(result.error)
          }

          return null
        }),
        // Makes sure the observable emits on the first alert, not the second (due to pairwise)
        startWith(null),
        // Pairs the previous alert and the next alert.
        pairwise(),
      )
      // Shows the alert, dismisses the previous one.
      .subscribe(([previousAlert, alert]) => {
        previousAlert?.dismiss()
        alert?.show()
      })
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this.subscription.unsubscribe()
  }

  /**
   * Whether the current workspace
   * has the snoozing feature enabled
   */
  @computed
  get isSnoozingEnabled() {
    return this.member.workspace.features.notificationsConfig.allowSnoozing
  }

  /**
   * Sets the types based on the preferences.
   *
   * @param prefs
   */
  @action.bound
  fromPreferences(prefs: NotificationPreferences) {
    const setFromMediaPrefs = (
      type: NotificationType,
      mediaPrefs?: NotificationMediaPreferences,
    ) => {
      type.set(NotificationMedium.Push, Boolean(mediaPrefs?.push?.enabled))
      type.set(NotificationMedium.Email, Boolean(mediaPrefs?.email?.enabled))
      type.setSnoozeDate(
        NotificationMedium.Push,
        maybeParseDate(mediaPrefs?.push?.snoozed_until),
      )
      type.setSnoozeDate(
        NotificationMedium.Email,
        maybeParseDate(mediaPrefs?.email?.snoozed_until),
      )
    }

    setFromMediaPrefs(this.master, prefs.media)
    this.types.forEach((type) => {
      setFromMediaPrefs(type, prefs.types_media[type.id])
    })
  }

  /**
   * Fetches the member's notification preferences.
   */
  fetchNotificationPreferences = task(
    once(async () => {
      const prefs = await this.notificationsPort.getPreferences(this.member)
      this.fromPreferences(prefs)
    }),
  )

  /**
   * Queues a save, returns a Promise for when the tail save completes.
   */
  async queueSave() {
    this.saveRequests.next(undefined)
    return firstValueFrom(this.sync$)
  }

  /**
   * Saves the updated preferences.
   *
   * @param prefs
   */
  async save(prefs: NotificationPreferences) {
    await this.notificationsPort.savePreferences(this.member, prefs)
  }
}

/**
 * Types of medium.
 */
export const enum NotificationMedium {
  Push,
  Email,
}

/**
 * Notification type.
 */
export class NotificationType {
  /**
   * Media.
   */
  @observable private mediaState = new Map<NotificationMedium, boolean>()
  @observable private mediaSnoozeState = new Map<
    NotificationMedium,
    Date | undefined
  >()

  /**
   * Constructor.
   */
  constructor(
    public readonly id: string,
    public readonly title: string,
    public readonly description: string,
    private master: NotificationType | null,
    private onSave: () => Promise<SaveResult>,
  ) {
    makeObservable(this)
  }

  /**
   * Whether the medium is enabled.
   *
   * @param medium
   */
  isEnabled = (medium: NotificationMedium) => {
    return !!this.mediaState.get(medium)
  }

  /**
   * The medium snooze until date
   *
   * @param medium
   */
  mediumSnoozeDate(medium: NotificationMedium) {
    return this.mediaSnoozeState.get(medium)
  }

  /**
   * Whether the medium is noozed
   *
   * The medium is snoozed if the set snoozed date
   * is later than the current date
   */
  isMediumSnoozed(medium: NotificationMedium) {
    const snoozeDate = this.mediaSnoozeState.get(medium)
    return snoozeDate ? snoozeDate > new Date() : false
  }

  isAnyMediumSnoozed() {
    return [NotificationMedium.Email, NotificationMedium.Push]
      .map((medium) => {
        return this.isMediumSnoozed(medium)
      })
      .some((maybeSnoozed) => maybeSnoozed === true)
  }

  /**
   * Whether we are allowed to toggle the medium
   *
   * @param medium
   */
  canToggleMedium = (medium: NotificationMedium) => {
    if (!this.master) {
      // This is the master, so can always toggle.
      return true
    }

    // This is a specific type, and we can only toggle it if the master is enabled for this medium.
    return this.master.isEnabled(medium)
  }

  /**
   * Whether we are allowed to snoozed the medium
   */
  canSnoozeMedium = (medium: NotificationMedium) => {
    if (!this.master) {
      return true
    }

    return this.master.isMediumSnoozed(medium)
  }

  /**
   * Whether we can snooze all media for this type.
   */
  canSnooze() {
    if (!this.master) {
      // This is the master, so we can always snooze
      return true
    }

    // If master is snoozed we can't change snoozing for a specific type.
    //
    // This could also have worked if we checked for every medium to be
    // snoozed in master instead of any medium since we always snooze
    // all media at once. But we keep this check here in case
    // one of the other portals somehow only snoozed one individual medium.
    return !this.master.isAnyMediumSnoozed()
  }

  /**
   * Sets the medium.
   *
   * @param medium
   * @param enabled
   */
  @action.bound
  set(medium: NotificationMedium, enabled: boolean) {
    this.mediaState.set(medium, enabled)
  }

  @action.bound
  setSnoozeDate(medium: NotificationMedium, snoozeDate: Date | undefined) {
    this.mediaSnoozeState.set(medium, snoozeDate)
  }

  /**
   * Toggles the medium.
   *
   * @param medium
   */
  toggleMedium = async (medium: NotificationMedium) => {
    const isEnabled = this.isEnabled(medium)
    this.set(medium, !isEnabled)
    const result = await this.onSave()
    if (result.type === 'Failed') {
      // If saving fails, revert.
      this.set(medium, isEnabled)
    }
  }

  /**
   * Snooze the medium until specified date
   *
   * @param medium
   * @param snoozeUntil
   */
  async snoozeMedium(medium: NotificationMedium, snoozeUntil: Date) {
    const prevSnoozeDate = this.mediumSnoozeDate(medium)
    this.setSnoozeDate(medium, snoozeUntil)
    const result = await this.onSave()
    if (result.type === 'Failed') {
      this.setSnoozeDate(medium, prevSnoozeDate)
    }
  }

  async snoozeAll(snoozeUntil: Date) {
    const emailSnoozeDate = this.mediumSnoozeDate(NotificationMedium.Email)
    const pushSnoozeDate = this.mediumSnoozeDate(NotificationMedium.Push)

    this.setSnoozeDate(NotificationMedium.Email, snoozeUntil)
    this.setSnoozeDate(NotificationMedium.Push, snoozeUntil)

    const result = await this.onSave()
    if (result.type === 'Failed') {
      this.setSnoozeDate(NotificationMedium.Email, emailSnoozeDate)
      this.setSnoozeDate(NotificationMedium.Push, pushSnoozeDate)
    }
  }

  /**
   * Unsnoozes medium
   *
   * To unsnooze we set the snooze until date
   * to the current date.
   * @param medium
   */
  async unsnoozeMedium(medium: NotificationMedium) {
    await this.snoozeMedium(medium, new Date())
  }

  async unsoozeAll() {
    await this.snoozeAll(new Date())
  }
}

/**
 * Save result.
 */
type SaveResult = Success | Failed

/**
 * Successful save.
 */
interface Success {
  type: 'Success'
}

/**
 * Failed save.
 */
interface Failed {
  type: 'Failed'
  error: unknown
}
